import { ReactNode, KeyboardEventHandler } from "react";
import clsx from "clsx";
import { TabsValue, useTabsContext } from "./TabsContext";

export type TabsTriggerProps<T extends TabsValue = TabsValue> = {
  value: T;
  disabled?: boolean;
  className?: string;
  children: ReactNode;
};

const getSiblingTriggers = (listEl: HTMLElement) =>
  Array.from(listEl.querySelectorAll<HTMLButtonElement>('[role="tab"]')).filter(
    (el) => !el.disabled,
  );

export const TabsTrigger = <T extends TabsValue>({
  value: triggerValue,
  disabled,
  className,
  children,
}: TabsTriggerProps<T>) => {
  const { value, setValue, styles } = useTabsContext<T>();
  const isActive = value === triggerValue;

  const base = clsx(
    "inline-flex h-10 items-center justify-center gap-2 text-sm font-medium outline-none transition",
    styles.variant === "underline"
      ? "-mb-px border-b-2 px-1"
      : "rounded-lg px-4",
  );

  const focus =
    "focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white";

  // Blue-ish look by default
  const inactive =
    styles.variant === "blue"
      ? "text-blue-600 hover:text-blue-700 hover:bg-blue-100"
      : styles.variant === "underline"
        ? "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
        : "text-gray-700 hover:bg-gray-50";

  const active =
    styles.variant === "blue"
      ? "bg-blue-600 text-white shadow-sm"
      : styles.variant === "underline"
        ? "border-blue-600 text-blue-700"
        : "bg-gray-900 text-white shadow-sm";

  const onKeyDown: KeyboardEventHandler<HTMLButtonElement> = (e) => {
    const current = e.currentTarget;
    const list = current.closest<HTMLElement>('[role="tablist"]');
    if (!list) return;

    const triggers = getSiblingTriggers(list);
    const idx = triggers.indexOf(current);
    if (idx < 0) return;

    const focusAt = (nextIndex: number) => {
      triggers[nextIndex]?.focus();
      const nextValue = triggers[nextIndex]?.getAttribute("data-tab-value");
      if (nextValue) setValue(nextValue as T);
    };

    switch (e.key) {
      case "ArrowRight":
        e.preventDefault();
        focusAt((idx + 1) % triggers.length);
        break;
      case "ArrowLeft":
        e.preventDefault();
        focusAt((idx - 1 + triggers.length) % triggers.length);
        break;
      case "Home":
        e.preventDefault();
        focusAt(0);
        break;
      case "End":
        e.preventDefault();
        focusAt(triggers.length - 1);
        break;
      default:
        break;
    }
  };

  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      aria-controls={`tab-panel-${triggerValue}`}
      data-tab-value={triggerValue}
      disabled={disabled}
      onClick={() => setValue(triggerValue)}
      onKeyDown={onKeyDown}
      className={clsx(
        base,
        focus,
        disabled && "cursor-not-allowed opacity-50",
        isActive ? active : inactive,
        styles.triggerClassName,
        isActive && styles.activeTriggerClassName,
        className,
      )}
    >
      {children}
    </button>
  );
};
